if (!customElements.get("sticky-header")) {
  customElements.define(
    "sticky-header",
    class StickyHeader extends HTMLElement {
      constructor() {
        super();
        this.onScrollHandler = this.queueScroll.bind(this);
        this.queueMobileMenuSync = this.queueMobileMenuSync.bind(this);
        this.syncMobileMenu = this.syncMobileMenu.bind(this);
        this.onMobileBreakpointChange = this.onMobileBreakpointChange.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
        this.scrollFrame = 0;
        this.menuSyncFrame = 0;
      }

      connectedCallback() {
        this.header = this.closest(".section-header") || document.querySelector(".section-header");
        if (!this.header) return;

        this.headerElement = this.querySelector(".header");
        this.mobileMenu = this.querySelector(".mobile-menu");
        this.navigationWrapper = this.querySelector(".navigation-wrapper") || document.querySelector(".navigation-wrapper");
        this.mast = this.querySelector(".head-mast-wrap") || this.headerElement || this;
        this.mobileIcons = [...this.querySelectorAll(".head-slot-nav-link-account svg, .head-slot-cart-link svg")];
        this.alwaysSticky = this.dataset.alwaysOn === "true";
        this.stickyDisabled = this.getAttribute("disabled") === "true";
        this.headerBounds = { top: 0, bottom: this.header.offsetHeight || 0 };
        this.currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        this.isScrolled = this.header.classList.contains("section-header-scrolled");
        this.preventReveal = false;
        this.menuWasOpen = false;
        this.mobileHeaderHeight = 0;
        this.mainNavToggle = this.querySelector("main-nav-toggle");
        this.predictiveSearch = this.querySelector("predictive-search");
        this.mobileBreakpoint = window.matchMedia("(max-width: 989px)");

        if (!this.mobileBreakpoint.matches && typeof this.mainNavToggle?.enable === 'function') this.mainNavToggle.enable();
        this.applyStickyState();
        this.syncMobileIcons();

        this.hideHeaderOnScrollUp = () => { this.preventReveal = true; };
        this.addEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
        window.addEventListener("scroll", this.onScrollHandler, { passive: true });
        window.addEventListener("resize", this.queueMobileMenuSync, { passive: true });
        window.addEventListener("orientationchange", this.queueMobileMenuSync, { passive: true });
        window.addEventListener("pageshow", this.onPageShow);

        if (typeof this.mobileBreakpoint.addEventListener === "function") {
          this.mobileBreakpoint.addEventListener("change", this.onMobileBreakpointChange);
        } else {
          this.mobileBreakpoint.addListener(this.onMobileBreakpointChange);
        }

        this.menuObserver = new MutationObserver(this.queueMobileMenuSync);
        if (document.body) this.menuObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] });
        if (this.mobileMenu) this.menuObserver.observe(this.mobileMenu, { attributes: true, attributeFilter: ["class", "aria-hidden"] });

        if (!this.alwaysSticky) this.createObserver();
        this.syncMobileMenu();
      }

      disconnectedCallback() {
        this.removeEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
        window.removeEventListener("scroll", this.onScrollHandler);
        window.removeEventListener("resize", this.queueMobileMenuSync);
        window.removeEventListener("orientationchange", this.queueMobileMenuSync);
        window.removeEventListener("pageshow", this.onPageShow);
        if (this.mobileBreakpoint) {
          if (typeof this.mobileBreakpoint.removeEventListener === "function") {
            this.mobileBreakpoint.removeEventListener("change", this.onMobileBreakpointChange);
          } else {
            this.mobileBreakpoint.removeListener(this.onMobileBreakpointChange);
          }
        }
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        if (this.menuSyncFrame) cancelAnimationFrame(this.menuSyncFrame);
        this.menuObserver?.disconnect();
        this.boundaryObserver?.disconnect();
        this.releaseMobileMenu();
      }

      isMobileViewport() {
        return Boolean(this.mobileBreakpoint?.matches);
      }

      clearMobileStickyState() {
        if (!this.header) return;
        this.header.classList.remove("section-header-hidden", "section-header-sticky", "section-header-sticky-always", "animate");
        this.header.style.removeProperty("position");
        this.header.style.removeProperty("top");
      }

      applyStickyState() {
        if (!this.header) return;
        if (this.isMobileViewport()) {
          this.clearMobileStickyState();
          return;
        }
        if (this.stickyDisabled) return;
        if (this.alwaysSticky) {
          this.header.classList.add("section-header-sticky-always");
          this.header.classList.remove("section-header-hidden");
          this.header.style.setProperty("position", "sticky");
          this.header.style.setProperty("top", "0px");
        }
      }

      createObserver() {
        if (typeof IntersectionObserver !== "function") return;
        this.boundaryObserver = new IntersectionObserver((entries, observer) => {
          const rect = entries[0]?.boundingClientRect;
          if (rect) {
            this.headerBounds = {
              top: rect.top + window.scrollY,
              bottom: rect.bottom + window.scrollY,
            };
          }
          observer.disconnect();
        });
        this.boundaryObserver.observe(this.header);
      }

      queueScroll() {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          this.onScroll();
        });
      }

      queueMobileMenuSync() {
        if (this.menuSyncFrame) return;
        this.menuSyncFrame = requestAnimationFrame(() => {
          this.menuSyncFrame = 0;
          this.syncMobileMenu();
        });
      }

      onScroll() {
        if (!this.header) return;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        const scrolled = scrollTop > 0;
        if (scrolled !== this.isScrolled) {
          this.header.classList.toggle("section-header-scrolled", scrolled);
          this.isScrolled = scrolled;
        }

        if (this.alwaysSticky) {
          this.currentScrollTop = scrollTop;
          return;
        }

        if (this.isMobileViewport()) {
          if (!this.isMobileMenuOpen()) this.clearMobileStickyState();
          this.currentScrollTop = scrollTop;
          return;
        }

        if (this.isMobileMenuOpen()) {
          this.currentScrollTop = scrollTop;
          return;
        }
        if (this.predictiveSearch?.isOpen) return;
        if (this.navigationWrapper?.classList.contains("is-animating")) return;

        const boundary = Number.isFinite(this.headerBounds.bottom) ? this.headerBounds.bottom : 0;
        if (scrollTop > this.currentScrollTop && scrollTop > boundary) {
          this.hide();
        } else if (scrollTop < this.currentScrollTop && scrollTop > boundary) {
          if (!this.preventReveal && !this.stickyDisabled) {
            this.reveal();
          } else {
            clearTimeout(this.isScrolling);
            this.isScrolling = setTimeout(() => { this.preventReveal = false; }, 66);
            this.hide();
          }
        } else if (!this.stickyDisabled && scrollTop <= (this.headerBounds.top || 0)) {
          this.reset();
        } else if (this.stickyDisabled && scrollTop <= boundary) {
          this.reset();
        }
        this.currentScrollTop = scrollTop;
      }

      hide() {
        if (!this.header || this.stickyDisabled) return;
        if (this.isMobileViewport()) {
          this.clearMobileStickyState();
          return;
        }
        if (this.alwaysSticky) {
          this.header.classList.add("section-header-sticky-always");
          this.header.classList.remove("section-header-hidden");
          return;
        }
        this.header.classList.add("section-header-hidden", "section-header-sticky");
        if (typeof this.mainNavToggle?.enable === 'function') this.mainNavToggle.enable();
        this.closeHeaderDropdowns();
      }

      reveal() {
        if (!this.header || this.stickyDisabled) return;
        if (this.isMobileViewport()) {
          this.clearMobileStickyState();
          return;
        }
        if (this.isMobileMenuOpen()) {
          this.syncMobileMenu();
          return;
        }
        if (this.alwaysSticky) {
          this.header.classList.add("section-header-sticky-always");
          this.header.classList.remove("section-header-hidden");
          this.header.style.setProperty("position", "sticky");
          this.header.style.setProperty("top", "0px");
          return;
        }
        this.header.classList.add("section-header-sticky", "animate");
        this.header.classList.remove("section-header-hidden");
      }

      reset() {
        if (!this.header) return;
        this.header.classList.remove("section-header-hidden", "section-header-sticky", "animate");
        this.applyStickyState();
        if (typeof this.mainNavToggle?.enable === 'function') this.mainNavToggle.enable();
      }

      isMobileMenuOpen() {
        if (!this.mobileBreakpoint?.matches || !this.mobileMenu) return false;
        return document.body?.classList.contains("mobile-menu-open") ||
          this.mobileMenu.classList.contains("mobile-menu-open") ||
          this.mobileMenu.getAttribute("aria-hidden") === "false";
      }

      onPageShow() {
        if (document.body && this.mobileMenu) {
          const drawerExplicitlyClosed = this.mobileMenu.getAttribute("aria-hidden") === "true" &&
            !this.mobileMenu.classList.contains("mobile-menu-open");
          if (drawerExplicitlyClosed) {
            document.body.classList.remove("mobile-menu-open", "navigation-child-open");
          }
        }
        this.releaseMobileMenu();
        this.syncMobileMenu();
      }

      onMobileBreakpointChange() {
        this.releaseMobileMenu();
        this.syncMobileIcons();
        this.applyStickyState();
        this.syncMobileMenu();
      }

      syncMobileMenu() {
        if (!this.header) return;

        if (!this.mobileBreakpoint?.matches) {
          this.header.classList.remove("menu-open");
          this.releaseMobileMenu();
          this.applyStickyState();
          if (typeof this.mainNavToggle?.enable === 'function') this.mainNavToggle.enable();
          this.menuWasOpen = false;
          return;
        }

        const height = Math.max(60, Math.round(this.mast?.getBoundingClientRect().height || this.mast?.offsetHeight || 72));
        if (height !== this.mobileHeaderHeight) {
          document.documentElement.style.setProperty("--abmium-mobile-header-height", `${height}px`);
          this.mobileHeaderHeight = height;
        }

        const open = this.isMobileMenuOpen();
        this.header.classList.toggle("menu-open", open);
        if (!open) {
          this.releaseMobileMenu();
          this.applyStickyState();
          this.menuWasOpen = false;
          return;
        }

        this.header.classList.remove("section-header-hidden");
        [
          ["position", "fixed"], ["top", "0px"], ["left", "0px"], ["right", "0px"],
          ["width", "100%"], ["z-index", "1200"],
        ].forEach(([property, value]) => this.header.style.setProperty(property, value, "important"));

        if (this.mobileMenu) {
          const rules = {
            position: "fixed",
            top: "var(--abmium-mobile-header-height, 60px)",
            left: "0px",
            right: "0px",
            bottom: "0px",
            width: "100vw",
            height: "auto",
            "max-height": "none",
            "overflow-y": "auto",
            "overscroll-behavior": "contain",
            "-webkit-overflow-scrolling": "touch",
          };
          Object.entries(rules).forEach(([property, value]) => this.mobileMenu.style.setProperty(property, value, "important"));
          if (!this.menuWasOpen) {
            this.mobileMenu.scrollTop = 0;
            this.mobileMenu.querySelectorAll(".mobile-menu-panel, .mobile-menu-panel-state").forEach((panel) => { panel.scrollTop = 0; });
          }
        }
        this.menuWasOpen = true;
      }

      releaseMobileMenu() {
        if (this.header) {
          ["left", "right", "width", "z-index"].forEach((property) => this.header.style.removeProperty(property));
          if (!this.isMobileViewport() && !this.stickyDisabled && this.alwaysSticky) {
            this.header.style.setProperty("position", "sticky");
            this.header.style.setProperty("top", "0px");
          } else {
            this.header.style.removeProperty("position");
            this.header.style.removeProperty("top");
          }
        }
        if (this.mobileMenu) {
          ["position", "top", "left", "right", "bottom", "width", "height", "max-height", "overflow-y", "overscroll-behavior", "-webkit-overflow-scrolling"]
            .forEach((property) => this.mobileMenu.style.removeProperty(property));
        }
      }

      syncMobileIcons() {
        const mobile = this.mobileBreakpoint?.matches;
        (this.mobileIcons || []).forEach((icon) => {
          if (mobile) {
            icon.style.setProperty("width", "30px");
            icon.style.setProperty("height", "30px");
          } else {
            icon.style.removeProperty("width");
            icon.style.removeProperty("height");
          }
        });
      }

      closeHeaderDropdowns() {
        this.header?.querySelectorAll("localization-form").forEach((dropdown) => {
          if (typeof dropdown.hidePanel === "function") dropdown.hidePanel();
        });
      }
    },
  );
}

if (!customElements.get("main-nav-toggle")) {
  customElements.define(
    "main-nav-toggle",
    class MainNavToggle extends HTMLElement {
      constructor() {
        super();
        this.header = this.closest("#header") || document.querySelector("#header");
        this.stickyHeader = this.closest("sticky-header") || document.querySelector("sticky-header");
        this.button = this.querySelector(".button-main-nav-toggle");
        this.navigationWrapper = this.closest("sticky-header")?.querySelector(".navigation-wrapper") || document.querySelector(".navigation-wrapper");
        if (!this.header || !this.button || !this.navigationWrapper) return;

        this.navigationWrapper.style.setProperty("--navigation-height", `${this.navigationWrapper.clientHeight + 2}px`);
        this.button.addEventListener("click", () => this.onToggle());
      }

      onToggle(state) {
        if (!this.navigationWrapper || this.navigationWrapper.classList.contains("is-animating") || !this.stickyHeader) return;
        this.navigationWrapper.classList.add("is-animating");
        if (state === "collapse") this.collapse();
        else if (state === "expand") this.expand();
        else this.isCollapsed() ? this.expand() : this.collapse();
        setTimeout(() => this.navigationWrapper.classList.remove("is-animating"), 300);
      }

      enable() {
        if (!this.header) return;
        this.header.classList.add("header-nav-collapse-enabled");
        this.collapse();
      }

      disable() {
        if (!this.header || !this.header.classList.contains("header-nav-collapse-enabled")) return;
        this.header.classList.remove("header-nav-collapse-enabled");
        this.expand();
      }

      expand() {
        if (!this.header || !this.button) return;
        this.header.classList.remove("navigation-collapsed");
        this.button.setAttribute("aria-expanded", "true");
      }

      collapse() {
        if (!this.header || !this.button) return;
        this.classList.add("is-visible");
        this.header.classList.add("navigation-collapsed");
        this.button.setAttribute("aria-expanded", "false");
      }

      isCollapsed() {
        return Boolean(this.header?.classList.contains("navigation-collapsed"));
      }
    },
  );
}
